/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Florian Hartl <f.hartl@techdivision.com>
 */

import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import { Grid, MobileStepper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { nextButtonActive } from '../../../Signals/formStateSignal';

/**
 * FormStepperActions Component
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
function FormStepperActions(props) {
  const { t } = useTranslation();
  const {
    handleBack, activeStep, previousLabel, nextLabel, isMobile,
  } = props;
  const isLastStep = activeStep === 4;
  const isFirstStep = activeStep === 0;
  const renderMobileStepper = () => (
    <MobileStepper
      sx={{
        p: 2,
        background: '#222',
        flexDirection: isLastStep ? 'column' : 'row',
      }}
      variant={isLastStep ? null : 'text'}
      steps={5}
      activeStep={activeStep}
      nextButton={(
        <Button
          type="submit"
          variant="contained"
          size="small"
          sx={isLastStep ? { padding: '10px 30px' } : null}
          disabled={!nextButtonActive.value}
        >
          {t(nextLabel)}
          {' '}
          {isLastStep ? null : <KeyboardArrowRight />}
        </Button>
              )}
      backButton={
                activeStep > 0 ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBack}
                    disabled={isFirstStep}
                    sx={isLastStep ? { mb: 2 } : null}
                  >
                    <KeyboardArrowLeft />
                    {' '}
                    {t(previousLabel)}
                  </Button>
                ) : null
            }
    />
  );

  if (isMobile) {
    return renderMobileStepper();
  }

  return (
    <Grid
      container
      direction="row"
      justifyContent={!isFirstStep ? 'space-between' : 'end'}
      mt={isFirstStep ? 0 : 4}
    >
      {!isFirstStep && (
        <Button variant="outlined" disabled={isFirstStep} onClick={handleBack}>
          {t(previousLabel)}
        </Button>
      )}
      <Button type="submit" variant="contained" color="primary" disabled={!nextButtonActive.value}>
        {t(nextLabel)}
      </Button>
    </Grid>
  );
}

FormStepperActions.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  handleBack: PropTypes.func.isRequired,
  activeStep: PropTypes.number.isRequired,
  previousLabel: PropTypes.string.isRequired,
  nextLabel: PropTypes.string.isRequired,
};

export default FormStepperActions;
